import type { PropsWithChildren } from "react";

export const MarkdownComponents = {
  // Customize how lists are rendered
  ul: ({ children }: PropsWithChildren) => (
    <ul className="list-disc [ul_&]:list-[circle] [ul_ul_&]:list-[square] pl-6 my-2 space-y-1">{children}</ul>
  ),
  ol: ({ children }: PropsWithChildren) => <ol className="list-decimal pl-6 my-2 space-y-1">{children}</ol>,
  li: ({ children }: PropsWithChildren) => <li className="my-1">{children}</li>,

  // Customize paragraphs
  p: ({ children }: PropsWithChildren) => <p className="my-2">{children}</p>,

  // Add styling for code blocks
  code: ({ children, className }: PropsWithChildren<{ className?: string }>) => {
    const isBlock = className?.includes("language-");
    return isBlock ? (
      <pre className="bg-muted rounded-md p-3 my-2 overflow-x-auto  whitespace-pre-wrap">
        <code className={className}>{children}</code>
      </pre>
    ) : (
      <code className="bg-muted px-1.5 py-0.5 rounded-md text-xs">{children}</code>
    );
  },

  // Style headings
  h1: ({ children }: PropsWithChildren) => <h1 className="text-xl font-semibold my-3">{children}</h1>,
  h2: ({ children }: PropsWithChildren) => <h2 className="text-lg font-semibold my-2.5">{children}</h2>,
  h3: ({ children }: PropsWithChildren) => <h3 className="text-base font-semibold my-2">{children}</h3>,
  h4: ({ children }: PropsWithChildren) => <h4 className="text-sm font-semibold my-1.5">{children}</h4>,
  h5: ({ children }: PropsWithChildren) => <h5 className="text-xs font-semibold my-1">{children}</h5>,
  h6: ({ children }: PropsWithChildren) => <h6 className="text-xs font-semibold my-1">{children}</h6>,

  // Style links
  a: ({ children, href }: PropsWithChildren<{ href?: string }>) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">
      {children}
    </a>
  ),

  // Style blockquotes
  blockquote: ({ children }: PropsWithChildren) => (
    <blockquote className="border-l-2 border-primary/30 pl-4 italic my-3">{children}</blockquote>
  ),

  strong: ({ children }: PropsWithChildren) => <strong className="font-semibold">{children}</strong>,

  table: ({ children }: PropsWithChildren) => (
    <div className="my-3 overflow-x-auto border-x border-t rounded">
      <table className="w-full border-collapse m-0!">{children}</table>
    </div>
  ),
  th: ({ children }: PropsWithChildren) => (
    <th className="px-2 py-1 bg-muted text-left font-medium border-b border-border">{children}</th>
  ),
  td: ({ children }: PropsWithChildren) => <td className="px-2 py-1 border-b border-border">{children}</td>,
  tr: ({ children }: PropsWithChildren) => <tr className="hover:bg-muted/50 transition-colors">{children}</tr>,
  thead: ({ children }: PropsWithChildren) => <thead className="bg-muted">{children}</thead>,
  tbody: ({ children }: PropsWithChildren) => <tbody>{children}</tbody>,
  tfoot: ({ children }: PropsWithChildren) => <tfoot className="bg-muted/50">{children}</tfoot>,
  caption: ({ children }: PropsWithChildren) => <caption className="text-sm font-medium my-2">{children}</caption>,
};
